import { queryOptions } from "@tanstack/react-query";
import type { CareNavigationCarePlans } from "../../../../../services/control-plane/src/tools/dashboards/care-navigation-care-plans.types";
import { fetchCareNavigationJson } from "./dashboardsReactQuery";

export type CarePlansResponse = CareNavigationCarePlans & { cache: "HIT" | "MISS" | "STALE" };

function isCarePlansResponse(value: unknown): value is CarePlansResponse {
  if (
    !value ||
    typeof value !== "object" ||
    !("asOf" in value) ||
    typeof value.asOf !== "string" ||
    !("workload" in value) ||
    !Array.isArray(value.workload) ||
    !("outstanding" in value) ||
    !Array.isArray(value.outstanding) ||
    !("cache" in value) ||
    !["HIT", "MISS", "STALE"].includes(String(value.cache))
  )
    return false;
  return (
    value.workload.every(
      (row) =>
        row &&
        typeof row.navigatorName === "string" &&
        Number.isFinite(row.carespacesWithGoals) &&
        Number.isFinite(row.avgGoalsPerCarespace) &&
        Number.isFinite(row.avgInterventionsPerCarespace),
    ) &&
    value.outstanding.every(
      (row) =>
        row &&
        typeof row.navigatorName === "string" &&
        Number.isFinite(row.count) &&
        Array.isArray(row.carespaceIds) &&
        row.carespaceIds.every((id: unknown) => typeof id === "string") &&
        Array.isArray(row.records) &&
        row.records.every(
          (record: unknown) =>
            record &&
            typeof record === "object" &&
            "id" in record &&
            typeof record.id === "string" &&
            "navigatorName" in record &&
            typeof record.navigatorName === "string" &&
            "clinicName" in record &&
            typeof record.clinicName === "string",
        ),
    )
  );
}

export function careNavigationCarePlansQueryOptions() {
  return queryOptions({
    queryKey: ["dashboards", "care-navigation", "care-plans"],
    staleTime: 60_000,
    refetchInterval: 60_000,
    queryFn: async () => {
      const body = await fetchCareNavigationJson(
        "/api/v1/dashboards/care-navigation/care-plans",
        undefined,
        "Care plan data is temporarily unavailable.",
      );
      if (!isCarePlansResponse(body)) throw new Error("Care plan response was malformed");
      return body;
    },
  });
}
